import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  assignPlaceOwner,
  deleteBoatPlace,
  detachBoatsFromPlace,
  deleteBoatsForPlace,
  setPlaceStatus,
  transferBoatsToOwner,
  useBoatPlace,
  useBoatsForPlace,
} from '../lib/boats';
import { useAllMembers } from '../lib/members';
import OwnerChangeDialog from '../components/OwnerChangeDialog';

export default function AdminBoatPlaceDetail() {
  const { placeId = '' } = useParams();
  const navigate = useNavigate();
  const { place, loading } = useBoatPlace(placeId);
  const { members } = useAllMembers();
  const { boats } = useBoatsForPlace(placeId);
  const [pendingOwner, setPendingOwner] = useState<string | null | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | null>(null);

  if (loading) return <p>Laddar…</p>;
  if (!place) return <p>Plats hittades inte.</p>;

  const oldOwner = members.find((m) => m.id === place.currentOwnerUid);

  async function applyOwnerChange(action: 'transfer' | 'detach' | 'delete') {
    if (pendingOwner === undefined) return;
    setError(null);
    try {
      if (action === 'transfer' && pendingOwner) {
        await transferBoatsToOwner(placeId, pendingOwner);
      } else if (action === 'detach') {
        await detachBoatsFromPlace(placeId);
      } else if (action === 'delete') {
        await deleteBoatsForPlace(placeId);
      }
      await assignPlaceOwner(placeId, pendingOwner);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPendingOwner(undefined);
    }
  }

  async function handleOwnerSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const newOwner = (form.get('owner') as string) || null;
    if (newOwner === place?.currentOwnerUid) return;

    if (boats.length > 0) {
      setPendingOwner(newOwner);
      return;
    }
    try {
      await assignPlaceOwner(placeId, newOwner);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleStatusSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const status = form.get('status') as
      | 'active'
      | 'handed_over'
      | 'removed';
    const note = (form.get('statusNote') as string) || null;
    try {
      await setPlaceStatus(placeId, status, note);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <Link to="/admin/batplatser" className="text-sm underline">
          ← Tillbaka
        </Link>
        <button
          type="button"
          onClick={async () => {
            if (boats.length > 0) {
              setError('Kan inte radera plats med båtar. Frikoppla dem först.');
              return;
            }
            if (!window.confirm(`Radera plats ${place.number} permanent?`)) return;
            try {
              await deleteBoatPlace(placeId);
              navigate('/admin/batplatser');
            } catch (e) {
              setError((e as Error).message);
            }
          }}
          className="px-3 py-1 text-sm bg-red-700 text-white rounded"
        >
          Radera plats permanent
        </button>
      </div>
      <h1 className="text-xl font-bold">Plats {place.number}</h1>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <form onSubmit={handleOwnerSubmit} className="space-y-2">
        <label className="block">
          <span className="text-sm">Ägare</span>
          <select
            name="owner"
            defaultValue={place.currentOwnerUid ?? ''}
            className="mt-1 w-full rounded border-gray-300 px-2 py-2"
          >
            <option value="">(ingen)</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName} — {m.email}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="px-3 py-2 bg-navy text-white rounded">
          Spara ägare
        </button>
      </form>

      <form onSubmit={handleStatusSubmit} className="space-y-2 border-t pt-4">
        <label className="block">
          <span className="text-sm">Status</span>
          <select
            name="status"
            defaultValue={place.status}
            className="mt-1 w-full rounded border-gray-300 px-2 py-2"
          >
            <option value="active">Aktiv</option>
            <option value="handed_over">Överlämnad</option>
            <option value="removed">Avförd</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm">Anteckning</span>
          <input
            name="statusNote"
            defaultValue={place.statusNote ?? ''}
            className="mt-1 w-full rounded border-gray-300 px-2 py-1"
          />
        </label>
        <button type="submit" className="px-3 py-2 bg-navy text-white rounded">
          Spara status
        </button>
      </form>

      <section className="border-t pt-4">
        <h2 className="font-bold mb-2">Båtar på platsen ({boats.length})</h2>
        <ul className="text-sm space-y-1">
          {boats.length === 0 && <li>(inga)</li>}
          {boats.map((b) => (
            <li key={b.id}>
              <Link
                to={`/admin/medlemmar/${b.ownerUid}/batar/${b.id}`}
                className="text-blue-700 underline"
              >
                {b.name ?? '(namnlös)'} {b.model && `— ${b.model}`}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {pendingOwner !== undefined && (
        <OwnerChangeDialog
          placeNumber={place.number}
          oldOwnerName={
            oldOwner ? `${oldOwner.firstName} ${oldOwner.lastName}` : '(okänd)'
          }
          newOwnerName={
            pendingOwner
              ? (() => {
                  const m = members.find((x) => x.id === pendingOwner);
                  return m ? `${m.firstName} ${m.lastName}` : null;
                })()
              : null
          }
          boatCount={boats.length}
          onConfirm={(action) => void applyOwnerChange(action)}
          onCancel={() => setPendingOwner(undefined)}
        />
      )}
    </div>
  );
}
